import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../../BaseController';
import { HttpService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { ActionAPI, FieldAPI, FormAPI, InputAPI, ModalProps, ModalService, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { FragmentNames, ScoreMewsViewerAliases } from '../../../../codegen/index';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../../../constants/routes';

import { ScoreMEWSResponse } from '../../score-types';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import { ScoreAndIndexesService, ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { HealthProfessionalUser } from '../../../../app/authentication';

@Controller({fragment: FragmentNames.SCORE_MEWS_VIEWER})
export default class ScoreMewsViewerController extends BaseController {

  @Inject(ScoreMewsViewerAliases.FORM_SCORE_MEWS_VIEWER)
  private formAPI!: FormAPI;

  @Inject(ScoreMewsViewerAliases.INPUT_EVATUALION_DATE)
  private inputDateAPI!: InputAPI;

  @Inject(ScoreMewsViewerAliases.TEXT_CONSCIOUSNESS_LEVEL)
  private textConsciounessAPI!: TextAPI;

  @Inject(ScoreMewsViewerAliases.FIELD_CONSCIOUSNESS_LEVEL)
  private fieldConsciousnessAPI!: FieldAPI;

  @Inject(ScoreMewsViewerAliases.TEXT_DESCRIPTION)
  private textDescriptionAPI!: TextAPI;

  @Inject(ScoreMewsViewerAliases.TEXT_HEART_RATE)
  private textHeartRateAPI!: TextAPI;

  @Inject(ScoreMewsViewerAliases.FIELD_HEART_RATE)
  private fieldHeartRateAPI!: FieldAPI;

  @Inject(ScoreMewsViewerAliases.TEXT_PROFESSIONAL)
  private textProfessionalAPI!: TextAPI;

  @Inject(ScoreMewsViewerAliases.TEXT_PUNCTUATION)
  private textPunctuationAPI!: TextAPI;

  @Inject(ScoreMewsViewerAliases.TEXT_RESPIRATORY_RATE)
  private textRespiratoryAPI!: TextAPI;

  @Inject(ScoreMewsViewerAliases.FIELD_RESPIRATORY_RATE)
  private fieldRespiratoryAPI!: FieldAPI;

  @Inject(ScoreMewsViewerAliases.TEXT_SYSTOLIC_PA)
  private textSystolicPAAPI!: TextAPI;

  @Inject(ScoreMewsViewerAliases.FIELD_SYSTOLIC_PA)
  private fieldSystolicPAAPI!: FieldAPI;

  @Inject(ScoreMewsViewerAliases.TEXT_TEMPERATURE)
  private textTemperatureAPI!: TextAPI;

  @Inject(ScoreMewsViewerAliases.FIELD_TEMPERATURE)
  private fieldTemperatureAPI!: FieldAPI;

  @Inject(ScoreMewsViewerAliases.ACTION_INACTIVATE_SCORE_MEWS)
  private actionInactivateAPI!: ActionAPI;

  @Inject(ScoreMewsViewerAliases.ACTION_NEW_SCORE_MEWS)
  private actionNewAPI!: ActionAPI;

  @Inject(ScoreMewsViewerAliases.ACTION_REFERENCE_SCORE_MEWS)
  private actionReferenceAPI!: ActionAPI;

  @OnMount({ target: ScoreMewsViewerAliases.LAYOUT_SCORE_MEWS_VIEWER })
  handleMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    InformationBarUtils.collapsedBar = true;
    this.context.set({encounterId, patientId});
    ScoreListContext.currentTab = ScoreType.SCORE_MEWS
  }

  private handleVisibleFields(record: ScoreMEWSResponse): void {
    if (!record.freqCardiaca) {
      this.fieldHeartRateAPI.setVisible(false);
    }
    if (!record.freqRespiratoria) {
      this.fieldRespiratoryAPI.setVisible(false);
    }
    if (!record.paSistolica) {
      this.fieldSystolicPAAPI.setVisible(false);
    }
    if (!record.temperatura) {
      this.fieldTemperatureAPI.setVisible(false);
    }
    if (!record.consciousnessLevel) {
      this.fieldConsciousnessAPI.setVisible(false);
    }
  }

  @OnMount({target: ScoreMewsViewerAliases.FORM_SCORE_MEWS_VIEWER})
  handleMountForm(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    const sequenceNumber = idScoreIndexes;
    const allowInfo = InformationBarUtils.allowInfo && ScoreAndIndexesService.isAllowEdit;

    this.formAPI.setLoading(true);

    HttpService.get(Endpoint.SCORE_MEWS, { sequenceNumber })
      .then(response => {
        const record = response.body as ScoreMEWSResponse;
        const user = foundationContext.getUser() as HealthProfessionalUser;
        const date = DateFormatterUtil.timeStamp(String(record.dateEvaluation));
        const professional = record.professionalName || '';
        const score = record.scoreQuantity as unknown as string || '0';
        const lvlConsciousness = record.consciousnessLevel ? record.consciousnessLevel.description : '';
        const heartRate = record.freqCardiaca as unknown as string || '';
        const respiratoryRate = record.freqRespiratoria as unknown as string || '';
        const systolicPA = record.paSistolica as unknown as string || '';
        const result = record.result as unknown as string || '';
        const temperature = record.temperatura as unknown as string || '';
        const showInactivate = record.professionalCode === user.physicalPersonId && Boolean(record.showInactivate) && allowInfo;

        this.inputDateAPI.setValue(date);
        this.textProfessionalAPI.setValue(professional);
        this.textPunctuationAPI.setValue(score);
        this.textConsciounessAPI.setValue(lvlConsciousness);
        this.textHeartRateAPI.setValue(heartRate);
        this.textRespiratoryAPI.setValue(respiratoryRate);
        this.textSystolicPAAPI.setValue(systolicPA);
        this.textDescriptionAPI.setValue(result);
        this.textTemperatureAPI.setValue(temperature);
        this.actionInactivateAPI.setEnabled(showInactivate);

        this.handleVisibleFields(record);
      }).catch(err => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      })
      .finally(() => {
        this.formAPI.setLoading(false);
        this.actionNewAPI.setEnabled(allowInfo);
        this.actionReferenceAPI.setEnabled(allowInfo);
      });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-pews-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private getReference(): void {
    const scoreType = 'mews';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType,
    }

    ModalService.openFullscreen(modalProperties);
  }

  @OnClick({ target: ScoreMewsViewerAliases.ACTION_NEW_SCORE_MEWS})
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.SCORE_MEWS_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick({ target: ScoreMewsViewerAliases.ACTION_INACTIVATE_SCORE_MEWS})
  handleClickInactive(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-inactivate',
      id: 'score-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      idScoreIndexes,
    } as Record<string, Value>;

    ModalService.openDefault(modalProperties);
  }

  @OnClick({ target: ScoreMewsViewerAliases.ACTION_REFERENCE_SCORE_MEWS })
  handleClickReference(): void {
    this.getReference();
  }

  @OnUnmount(ScoreMewsViewerAliases.LAYOUT_SCORE_MEWS_VIEWER)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_MEWS;
  }
}
